import { useEffect, createContext, useContext, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FindBshop } from '../services/servicesHook/bshopServiceHook';
import { PATH_ECOMMERCE } from '../routes/paths';
import { useDispatch } from 'react-redux'; // Importa useDispatch
import { resetCart } from '../redux/slices/product'; // Importa la acción de resetear el carrito

const BshopContext = createContext({
  bshopData: null,
  loading: true,
  error: null,
});

export const useBshopContext = () => useContext(BshopContext);

export const BshopProvider = ({ children }) => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch(); // Usamos dispatch

  const [bshopId, setBshopId] = useState(params.bshopId); // estado controlado de bshopId
  const [bshopData, setBshopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirección o recuperación desde localStorage si no hay bshopId en la URL
  useEffect(() => {
    if (!params.bshopId) {
      const stored = localStorage.getItem('bshopSelect');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed?.bshop_id) {
            setBshopId(parsed.bshop_id); // actualiza el estado para que FindBshop use este valor
            return;
          }
        } catch (err) {
          console.error('Error parseando bshopSelect:', err);
        }
      }
      // Si no hay ni en URL ni en localStorage → redirige
      navigate(PATH_ECOMMERCE.eCommerce.root);
    }
  }, [params.bshopId, navigate]);

  // Limpiar localStorage y carrito cuando bshopId cambie
  useEffect(() => {
    if (bshopId) {
      localStorage.removeItem('bshopSelect'); // Elimina el valor anterior de localStorage
      dispatch(resetCart()); // Limpiar el carrito usando Redux
    }
  }, [bshopId, dispatch]); // Solo se dispara cuando bshopId cambie

  const { data, isLoading, isError } = FindBshop({
    bshop_id: bshopId,
    bshop_status: '1',
  });

  // Cargar desde localStorage si coincide el ID
  useEffect(() => {
    const loadFromLocalStorage = () => {
      try {
        const stored = localStorage.getItem('bshopSelect');
        if (stored) {
          const parsed = JSON.parse(stored);
          const isValidStored = parsed?.isValid && parsed?.bshop_id === bshopId;
          if (isValidStored) {
            setBshopData(parsed);
            setLoading(false);
            return true;
          }
        }
      } catch (err) {
        console.error('Error leyendo bshopSelect del localStorage:', err);
      }
      return false;
    };

    const foundInStorage = loadFromLocalStorage();
    if (!foundInStorage) {
      setLoading(true);
    }
  }, [bshopId]);

  useEffect(() => {
    if (!isLoading) {
      if (data) {
        const isValid = data?.bshop_id === bshopId;
        const enhancedData = { ...data, isValid };
        setBshopData(enhancedData);
        localStorage.setItem('bshopSelect', JSON.stringify(enhancedData));
        setError(null);
      } else if (isError || !data) {
        setError('No se pudo cargar la tienda. Redirigiendo...');
        navigate(PATH_ECOMMERCE.eCommerce.root);
      }
      setLoading(false);
    }
  }, [isLoading, data, isError, bshopId, navigate]);

  return (
    <BshopContext.Provider value={{ bshopData, loading, error }}>
      {children}
    </BshopContext.Provider>
  );
};
